import { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { Option } from '@/lib/v2/components/Select/Select';

import { ContactStatus } from '@/src/ContactsModule/interfaces/Interfaces';
import { ServiceMethods } from '@/src/infrastructure/Protocol/EmblueService';
import { useEmblue } from '@/src/infrastructure/Protocol/useEmblue';
import { Channel, ListProfile, Tag } from '@/src/modules/ContactsModule/types/Segments';

export const useGetListValuesOptions = () => {
  const { t } = useTranslation();
  const [userGroupsOptions, setUserGroupsOptions] = useState<Option[]>();
  const [tagsOptions, setTagsOptions] = useState<Option[]>();
  const [channelsOptions, setChannelsOptions] = useState<Option[]>();
  const [geoLocalizationOptions, setGeoLocalizationOptions] = useState<Option[]>();
  const [devicesOptions, setDevicesOptions] = useState<Option[]>();
  const [socialNetworkOptions, setSocialNetworkOptions] = useState<Option[]>();
  const [genderOptions, setGenderOptions] = useState<Option[]>();

  const rankList: Option[] = useMemo(
    () => [
      {
        id: ContactStatus.discarded,
        name: t('STATUS_FILTER.discarded'),
        value: ContactStatus.discarded,
      },
      {
        id: ContactStatus.inactive,
        name: t('STATUS_FILTER.inactive'),
        value: ContactStatus.inactive,
      },
      { id: ContactStatus.new, name: t('STATUS_FILTER.new'), value: ContactStatus.new },
      {
        id: ContactStatus.frequent,
        name: t('STATUS_FILTER.frequent'),
        value: ContactStatus.frequent,
      },
      { id: ContactStatus.asleep, name: t('STATUS_FILTER.asleep'), value: ContactStatus.asleep },
    ],
    [t]
  );

  const [valuesOptions] = useEmblue(ServiceMethods.getValuesSegmentsFiltersContacts);

  const mapValuesOptionsUserGroups = (userGroupData: Array<string[]>): Option[] => {
    return userGroupData.map((group) => ({
      id: Number(group[0]),
      name: group[1],
      value: group[0],
    }));
  };

  const mapValuesOptionsTags = (tagsData: Tag[]): Option[] => {
    return tagsData.map((tag) => ({ id: tag.tagId, name: tag.name, value: tag.tagId }));
  };

  const mapValuesOptionsChannels = (channelsData: Channel[]): Option[] => {
    return channelsData.map((channel) => ({
      id: channel.channelTypeId,
      name: channel.description,
      value: channel.channelTypeId,
    }));
  };

  const mapValuesOptionsListProfiles = (
    listProfiles: ListProfile[],
    keyValue: string
  ): Option[] => {
    return listProfiles
      .find((listProfile) => listProfile.Key === keyValue)
      ?.Value.map((item) => ({ id: item.Key, name: item.Value, value: item.Key })) as Option[];
  };

  useEffect(() => {
    if (!valuesOptions) return;
    const { userGroups, tags, channels, listProfiles } = valuesOptions;
    setUserGroupsOptions(mapValuesOptionsUserGroups(userGroups));
    setTagsOptions(mapValuesOptionsTags(tags));
    setChannelsOptions(mapValuesOptionsChannels(channels));
    /* cspell:disable */
    setGeoLocalizationOptions(mapValuesOptionsListProfiles(listProfiles, 'paises'));
    setDevicesOptions(mapValuesOptionsListProfiles(listProfiles, 'dispositivos'));
    setSocialNetworkOptions(mapValuesOptionsListProfiles(listProfiles, 'redes_sociales'));
    //setRankOptions(mapValuesOptionsListProfiles(listProfiles, 'dominios'));
    setGenderOptions(mapValuesOptionsListProfiles(listProfiles, 'sexo'));
  }, [valuesOptions]);

  return {
    isLoading:
      !userGroupsOptions ||
      !tagsOptions ||
      !channelsOptions ||
      !geoLocalizationOptions ||
      !devicesOptions ||
      !socialNetworkOptions ||
      !rankList ||
      !genderOptions,
    userGroups: userGroupsOptions,
    tags: tagsOptions,
    channels: channelsOptions,
    getLocalization: geoLocalizationOptions,
    devices: devicesOptions,
    socialNetwork: socialNetworkOptions,
    rank: rankList,
    gender: genderOptions,
  };
};
